import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';

import {
  HaynesProElectronicLocationItemModel,
  HaynesProElectronicLocationSystemModel
} from '../typings/haynes-pro.diagnostic-tool.electronic-location-details';

import { HaynesProService }                 from '../haynes-pro.service';
import { HaynesProSvgInteractionDirective } from '../shared/haynes-pro.svg-interaction.directive';

@Component({
  selector   : 'wor-haynes-pro-diagnostic-tool-electronic-location-details',
  styles: [ require('./haynes-pro.diagnostic-tool.electronic-location-details.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.electronic-location-details.component.html')
})
export class HaynesProDiagnosticToolElectronicLocationDetailsComponent
  extends HaynesProSvgInteractionDirective
  implements AfterViewInit, OnChanges {

  @Input() autoHighlightLocation = '';
  @Input() location              : HaynesProElectronicLocationSystemModel;
  @Input() maxHeight             = '70vh';

  @ViewChild('itemsContainer', { static: false }) itemsContainer : ElementRef<HTMLDivElement>;
  @ViewChildren('itemRow') itemRows                              : QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild('svgContainer', { static: false }) svgContainer     : ElementRef<HTMLDivElement>;
  @ViewChild('zoomViewport', { static: false }) zoomViewport     : ElementRef<HTMLDivElement>;

  selectedItemId : number = null;
  hoveredItemId  : number = null;
  loadingSvg              = false;
  svgError                = '';
  zoomLevel               = 1;
  svgBaseWidth            = 0;
  svgBaseHeight           = 0;

  private readonly ZOOM_STEPS       = HaynesProService.ELECTRONIC_LOCATION_ZOOM_STEPS;
  private readonly allowedMatchTags = HaynesProService.ELECTRONIC_LOCATION_SVG_MATCH_TAGS;
  private readonly fuseTypes        = HaynesProService.ELECTRONIC_LOCATION_FUSE_TYPES;
  private readonly fuseColorMap     = HaynesProService.ELECTRONIC_LOCATION_FUSE_COLOR_MAP;

  constructor () {
    super();
  }

  ngAfterViewInit () : void {
    setTimeout(() => {
      this.loadInteractiveSvg();
    });
  }

  ngOnChanges ( changes : SimpleChanges ) : void {
    if (changes.location && !changes.location.isFirstChange()) {
      this.selectedItemId = null;
      this.hoveredItemId  = null;
      this.zoomLevel      = 1;
      this.svgBaseWidth   = 0;
      this.svgBaseHeight  = 0;

      setTimeout(() => {
        this.loadInteractiveSvg();
      });
    }
  }

  get interactiveItems () : Array<HaynesProElectronicLocationItemModel> {
    return (this.location?.items || []).filter(( item : HaynesProElectronicLocationItemModel ) => item.type !== 'loc');
  }

  get infoItems () : Array<HaynesProElectronicLocationItemModel> {
    return (this.location?.items || []).filter(( item : HaynesProElectronicLocationItemModel ) => item.type === 'loc');
  }

  get hasPositionImage () : boolean {
    return !!this.location?.system_position_image?.image_path;
  }

  protected getInteractiveItemIds () : Array<number> {
    return this.interactiveItems
    .map(( item : HaynesProElectronicLocationItemModel ) => Number(item.id))
    .filter(( id : number ) => !!id && !isNaN(id));
  }

  protected getMatchedSvgElements ( itemId : number ) : Array<Element> {
    return this.getSvgElements(itemId);
  }

  protected getHoveredItemId () : number | null {
    return this.hoveredItemId;
  }

  protected getSelectedItemId () : number | null {
    return this.selectedItemId;
  }

  protected handleResolvedSvgClick ( itemId : number ) : void {
    this.selectedItemId = this.selectedItemId === itemId
      ? null
      : itemId;

    this.applySvgState();
    this.scrollSelectedItemIntoView();
  }

  protected setHoveredItemId ( itemId : number | null ) : void {
    this.hoveredItemId = itemId;
  }

  handleItemHover ( item : HaynesProElectronicLocationItemModel ) : void {
    this.hoveredItemId = Number(item.id) || null;
    this.applySvgState();
  }

  handleItemLeave ( item : HaynesProElectronicLocationItemModel ) : void {
    if (this.hoveredItemId === Number(item.id)) {
      this.hoveredItemId = null;
      this.applySvgState();
    }
  }

  selectItem ( item : HaynesProElectronicLocationItemModel ) : void {
    const numericId = Number(item.id);

    this.selectedItemId = this.selectedItemId === numericId
      ? null
      : numericId;

    this.applySvgState();
    this.scrollSelectedItemIntoView();
  }

  isItemSelected ( item : HaynesProElectronicLocationItemModel ) : boolean {
    return this.selectedItemId === Number(item.id);
  }

  isItemHovered ( item : HaynesProElectronicLocationItemModel ) : boolean {
    return this.hoveredItemId === Number(item.id);
  }

  isFuseType ( item : HaynesProElectronicLocationItemModel ) : boolean {
    return this.fuseTypes.includes(item.type);
  }

  getItemTypeLabel ( item : HaynesProElectronicLocationItemModel ) : string {
    switch (item.type) {
      case 'fus': return 'Fuse';
      case 'fut': return 'Fuse';
      case 'fuu': return 'Fuse';
      case 'fux': return 'Fuse';
      case 'rel': return 'Relay';
      case 'mmp': return 'Component';
      case 'cm' : return 'Control Unit';
      case 'g'  : return 'Ground';
      case 'ac' : return 'A/C';
      case 'oth': return 'Other';
      default   : return item.type?.toUpperCase() || '';
    }
  }

  getFuseColor ( item : HaynesProElectronicLocationItemModel ) : string {
    if (item.type !== 'fus' || !item.value) {
      return '';
    }

    return this.resolveFuseColor(item.value);
  }

  getFormattedDescription ( item : HaynesProElectronicLocationItemModel ) : string {
    return (item.description || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\s*\[[A-Z]{1,3}\d+\]\s*$/, '');
  }

  getItemCode ( item : HaynesProElectronicLocationItemModel ) : string {
    if (item.oem_name) {
      return item.oem_name;
    }

    const match = (item.description || '').match(/\[([A-Z]{1,3}\d+)\]\s*$/);

    return match ? match[1] : '';
  }

  zoomIn () : void {
    const nextStep = this.ZOOM_STEPS.find(( step : number ) => step > this.zoomLevel);

    this.zoomLevel = nextStep !== undefined ? nextStep : this.zoomLevel;
  }

  zoomOut () : void {
    const prevSteps = this.ZOOM_STEPS.filter(( step : number ) => step < this.zoomLevel);

    this.zoomLevel = prevSteps.length ? prevSteps[prevSteps.length - 1] : this.zoomLevel;
  }

  resetZoom () : void {
    this.zoomLevel = 1;
  }

  get zoomPercent () : number {
    return Math.round(this.zoomLevel * 100);
  }

  trackByItem ( _index : number, item : HaynesProElectronicLocationItemModel ) : string {
    return item?.id;
  }

  protected applySvgState () : void {
    super.applySvgState();
    this.updateAmperageArrow();
  }

  private resolveFuseColor ( value : number ) : string {
    const key = String(value);

    if (this.fuseColorMap[key]) {
      return this.fuseColorMap[key];
    }

    const rounded = String(Math.round(value));

    return this.fuseColorMap[rounded] || '';
  }

  private colorAllFuseElements () : void {
    if (!this.svgElement) {
      return;
    }

    this.interactiveItems.forEach(( item : HaynesProElectronicLocationItemModel ) => {
      if (item.type !== 'fus' || !item.value) {
        return;
      }

      const color = this.resolveFuseColor(item.value);

      if (!color) {
        return;
      }

      const elements = this.findSvgElementsForLocation(item);

      elements.forEach(( element : Element ) => {
        const targets = this.getSvgInteractionTargets(element);

        targets.forEach(( target : SVGElement ) => {
          if (target.tagName.toLowerCase() === 'text') {
            return;
          }

          target.style.fill = color;
        });
      });
    });
  }

  private updateAmperageArrow () : void {
    this.hideAllAmperageArrows();

    if (!this.selectedItemId) {
      return;
    }

    const item = this.interactiveItems
    .find(( i : HaynesProElectronicLocationItemModel ) => Number(i.id) === this.selectedItemId);

    if (!item || !this.isFuseType(item) || !item.value) {
      return;
    }

    this.showAmperageArrow(item.value, item.type);
  }

  private getArrowIdsForAmperage ( amperage : number, fuseType : string ) : Array<string> {
    const ids : Array<string> = [];

    if (fuseType === 'fux') {
      ids.push('arrow-maxi');
    }

    ids.push(`arrow-${amperage}`);

    if (amperage !== Math.round(amperage)) {
      ids.push(`arrow-${Math.round(amperage)}`);
    }

    if (amperage !== Math.floor(amperage)) {
      ids.push(`arrow-${Math.floor(amperage)}`);
    }

    return ids;
  }

  private findSvgElementById ( id : string ) : Element | null {
    return this.svgElement?.querySelector(`[id="${id}"]`) || null;
  }

  private showAmperageArrow ( amperage : number, fuseType : string ) : void {
    if (!this.svgElement || !amperage) {
      return;
    }

    const arrowIds = this.getArrowIdsForAmperage(amperage, fuseType);

    for (const id of arrowIds) {
      const element = this.findSvgElementById(id);

      if (element) {
        this.showArrowElement(element as SVGElement);

        return;
      }
    }
  }

  private hideAllAmperageArrows () : void {
    if (!this.svgElement) {
      return;
    }

    Array.from(this.svgElement.querySelectorAll('[id]'))
    .filter(( el : Element ) => /^arrow[-_]/i.test(el.id))
    .forEach(( el : Element ) => this.hideArrowElement(el as SVGElement));
  }

  private readonly ARROW_FILL_COLOR = '#000';

  private showArrowElement ( element : SVGElement ) : void {
    element.style.display    = '';
    element.style.visibility = 'visible';
    element.style.opacity    = '1';
    element.style.fill       = this.ARROW_FILL_COLOR;

    element.removeAttribute('display');
    element.setAttribute('fill', this.ARROW_FILL_COLOR);

    if (element.getAttribute('visibility') === 'hidden') {
      element.setAttribute('visibility', 'visible');
    }
  }

  private hideArrowElement ( element : SVGElement ) : void {
    element.style.fill = 'none';
    element.setAttribute('fill', 'none');
  }

  private findSvgElementsForLocation ( item : HaynesProElectronicLocationItemModel ) : Array<Element> {
    if (!this.svgElement || !item) {
      return [];
    }

    const type     = item.type;
    const location = item.location;

    if (!type || !location) {
      return [];
    }

    const baseId      = `${type}-${location}`;
    const capitalised = `${type}-${location.charAt(0).toUpperCase()}${location.slice(1)}`;
    const withSuffix  = `${baseId}_1_`;

    const selectors = [
      `#${CSS.escape(baseId)}`,
      `#${CSS.escape(capitalised)}`,
      `[id^="${CSS.escape(baseId)}"]`,
      `#${CSS.escape(withSuffix)}`,
      `[id="${CSS.escape(baseId)}"]`
    ];

    for (const selector of selectors) {
      try {
        const elements = Array.from(this.svgElement.querySelectorAll(selector))
        .filter(( element : Element ) => this.allowedMatchTags.includes(element.tagName.toLowerCase()));

        if (elements.length) {
          return Array.from(new Set(elements));
        }
      } catch {
        continue;
      }
    }

    return [];
  }

  private getSvgElements ( itemId : number ) : Array<Element> {
    if (!itemId) {
      return [];
    }

    if (!this.svgElementCache[itemId]) {
      const item = this.interactiveItems.find(( i : HaynesProElectronicLocationItemModel ) => Number(i.id) === itemId);

      this.svgElementCache[itemId] = item
        ? this.findSvgElementsForLocation(item)
        : [];
    }

    return this.svgElementCache[itemId];
  }

  private async fetchSvgAsText ( url : string ) : Promise<string> {
    const response = await fetch(url, { mode: 'cors' });

    if (!response.ok) {
      throw new Error(`Failed to fetch location diagram (${response.status}).`);
    }

    return response.text();
  }

  private computeSvgBaseDimensions () : void {
    const viewport = this.zoomViewport?.nativeElement;

    if (!viewport) {
      return;
    }

    this.svgBaseWidth  = viewport.clientWidth;
    this.svgBaseHeight = viewport.clientHeight;
  }

  private async loadInteractiveSvg () : Promise<void> {
    const imageUrl = this.location?.system_detail_image?.image_path;

    if (!imageUrl || !this.svgContainer?.nativeElement) {
      return;
    }

    this.loadingSvg = true;
    this.svgError   = '';

    try {
      const svgText = await this.fetchSvgAsText(imageUrl);

      this.injectInlineSvg(svgText, this.svgContainer);
      this.colorAllFuseElements();
      this.applyAutoHighlight();
      this.applySvgState();

      setTimeout(() => {
        this.computeSvgBaseDimensions();

        if (this.autoHighlightLocation) {
          this.zoomLevel = 1;
        }
      });
    } catch (err) {
      this.svgError = err?.message || 'Unable to load location diagram.';
    } finally {
      this.loadingSvg = false;
    }
  }

  private applyAutoHighlight () : void {
    if (!this.autoHighlightLocation) {
      return;
    }

    const code = this.autoHighlightLocation.toUpperCase();
    const item = this.interactiveItems
                  .find(( i : HaynesProElectronicLocationItemModel ) => i.location?.toUpperCase() === code);

    if (item) {
      const itemId = Number(item.id) || null;

      this.selectedItemId = itemId;

      // Mark the main component with a persistent highlight class
      if (itemId) {
        this.getMatchedSvgElements(itemId).forEach(( element : Element ) => {
          element.classList.add('hp-td-part--auto-highlighted');
        });
      }
    }
  }

  private scrollSelectedItemIntoView () : void {
    if (!this.selectedItemId || !this.itemsContainer?.nativeElement) {
      return;
    }

    setTimeout(() => {
      const container = this.itemsContainer?.nativeElement;

      if (!container) {
        return;
      }

      const selectedRow = this.itemRows?.find(( row : ElementRef<HTMLDivElement> ) =>
        row?.nativeElement?.dataset?.itemId === String(this.selectedItemId)
      )?.nativeElement;

      if (!selectedRow) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const rowRect       = selectedRow.getBoundingClientRect();

      const nextTop = container.scrollTop + (rowRect.top - containerRect.top);

      container.scrollTo({
        top     : Math.max(nextTop, 0),
        behavior: 'smooth'
      });
    });
  }
}
